import { useState, useEffect } from 'react';
import { X, Copy, Check, Share2, AlertCircle, Loader2 } from 'lucide-react';
import { shareChat, getExistingShare } from '../store/sharedChats$';
import './ShareChatModal.css';

export default function ShareChatModal({ isOpen, onClose, chatId }) {
  const [shareLink, setShareLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [checkingExisting, setCheckingExisting] = useState(false);

  // Check for existing share when modal opens
  useEffect(() => {
    if (isOpen && chatId) {
      setCheckingExisting(true);
      getExistingShare(chatId)
        .then((existing) => {
          if (existing?.share_token) {
            const link = `${window.location.origin}/shared/chat/${existing.share_token}`;
            setShareLink(link);
          }
        })
        .finally(() => setCheckingExisting(false));
    }
  }, [isOpen, chatId]);

  const handleShare = async () => {
    if (!chatId) return;
    
    setLoading(true);
    setError(null);

    try {
      const result = await shareChat(chatId);
      if (result?.shareToken) {
        const link = `${window.location.origin}/shared/chat/${result.shareToken}`;
        setShareLink(link);
      }
    } catch (err) {
      console.error('Failed to share:', err);
      setError('Failed to create share link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!shareLink) return;

    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleClose = () => {
    setShareLink(null);
    setCopied(false);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="share-modal-overlay" onClick={handleClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        <button className="share-modal-close" onClick={handleClose}>
          <X size={20} />
        </button>

        <div className="share-modal-header">
          <Share2 size={24} />
          <h2>Share Conversation</h2>
        </div>

        {checkingExisting ? (
          <div className="share-modal-loading">
            <Loader2 className="spin" size={20} />
            <span>Checking...</span>
          </div>
        ) : !shareLink ? (
          <>
            <div className="share-modal-warning">
              <AlertCircle size={18} />
              <div className="share-modal-warning-text">
                <strong>Notice:</strong> We currently don't support revoking links. 
                All future messages in this conversation will also be shared. 
                Please be careful when sharing.
              </div>
            </div>

            {error && <div className="share-modal-error">{error}</div>}

            <button
              className="share-modal-btn"
              onClick={handleShare}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="spin" size={16} />
                  <span>Generating...</span>
                </>
              ) : (
                'Generate Share Link'
              )}
            </button>
          </>
        ) : (
          <>
            <div className="share-modal-success">
              <p>Your conversation is shared! Copy the link below:</p>
            </div>

            <div className="share-modal-link">
              <input
                type="text"
                value={shareLink}
                readOnly
                onClick={(e) => e.target.select()}
              />
              <button onClick={handleCopy} title="Copy link">
                {copied ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>

            <div className="share-modal-info">
              <p>Anyone with this link can view this conversation.</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

